import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { ApiCoreService } from '@qtek/core/api-core';
import { MetaCoreStore } from '@qtek/libs/meta-core';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { UserModel } from '@qtek/shared/models';
import { catchError, of, Subject, take, tap } from 'rxjs';

interface AuthUserState {
  user: UserModel | null;
}

const initialState: AuthUserState = {
  user: null,
};

export const AuthUserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ user }) => ({
    isAuthorized: computed(() => !!user()?.person?.personId),
    personProgress: computed(() => user()?.person?.bitMasks?._prgs ?? 0),
    companyProgress: computed(() => user()?.company?.bitMasks?._prgs ?? 0),
    tutorialProgress: computed(() => user()?.person?.bitMasks?._uitut),
  })),
  withProps(() => ({
    loggedOut$: new Subject<void>(),
  })),
  withMethods(
    (
      store,
      apiCoreService = inject(ApiCoreService),
      metaCoreStore = inject(MetaCoreStore),
      snackBar = inject(SnackBarService)
    ) => ({
      setUser(user: UserModel | null): void {
        patchState(store, { user });
      },

      clearUser(): void {
        patchState(store, { user: null });
      },

      getUser(): void {
        apiCoreService
          .getUserData()
          .pipe(
            take(1),
            tap(response => {
              patchState(store, {
                user: response.sts?.sts === 'ERR' ? null : response.res,
              });
            }),
            catchError(() => {
              snackBar.showSnackBar({
                type: 'ErrorMessage',
                message: 'GNR_LOAD_USER_ERROR',
              } as ErrorSnackBarData);
              return of(null);
            })
          )
          .subscribe();
      },

      logout(queryParams?: string, skipRedirect = false): void {
        patchState(store, { user: null });
        store.loggedOut$.next();
        const authDomain = metaCoreStore.authGuiDomain();
        apiCoreService
          .logout()
          .pipe(
            tap(() => {
              if (!skipRedirect) {
                window.location.href = `${authDomain}/${queryParams ?? ''}`;
              }
            }),
            catchError(() => {
              if (!skipRedirect) {
                window.location.href = `${authDomain}/${queryParams ?? ''}`;
              }
              return of(null);
            })
          )
          .subscribe();
      },
    })
  )
);
